// schwabbadge-20261104: Schwab account in the wallet slot on stock charts.
// ownerkey-20261104: owner-only. The server returns accounts only with the
// private X-Owner-Key header; the key lives in localStorage.
// schwabconnect-20261104: Connect/Disconnect dropdown styled like the HL keys
// connect (local-wallet-* / lw-* classes). #ownerkey=<key> still works once.
import { useState, useEffect, useRef } from 'react'

export const BASE = import.meta.env.VITE_HTTP_PROXY_BASE || 'https://history.cheddarterminal.app'
export const KEY = 'solcheddar:schwabAcct'
export const OWNER = 'solcheddar:ownerKey'
const HIDE = 'solcheddar:schwabHideValue'
// acctvalue-20261104: value only when the server sends it (hidden otherwise).
const fmtVal = (v) => (typeof v === 'number' && isFinite(v))
  ? '$' + v.toLocaleString(undefined, { maximumFractionDigits: 0 }) : null

try {
  const m = window.location.hash.match(/(?:^#|&)ownerkey=([^&]+)/)
  if (m) {
    localStorage.setItem(OWNER, decodeURIComponent(m[1]))
    const rest = window.location.hash.replace(/(?:^#|&)ownerkey=[^&]+/, '').replace(/^&/, '')
    history.replaceState(null, '', window.location.pathname + window.location.search + (rest ? '#' + rest : ''))
  }
} catch { /* ignore */ }

const fetchAccts = (owner) =>
  fetch(`${BASE}/stk/accounts`, { headers: { 'X-Owner-Key': owner } })
    .then(r => (r.ok ? r.json() : Promise.reject(r.status)))

export default function SchwabAccountBadge({ relayUp, children }) {
  const [owner, setOwner] = useState(() => localStorage.getItem(OWNER) || '')
  const [accts, setAccts] = useState([])
  const [idx, setIdx] = useState(() => +(localStorage.getItem(KEY) || 0) || 0)
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [hide, setHide] = useState(() => localStorage.getItem(HIDE) === '1')
  const ref = useRef(null)

  useEffect(() => {
    if (!owner || !relayUp || accts.length) return
    let dead = false
    fetchAccts(owner).then(d => { if (!dead && Array.isArray(d)) setAccts(d) }).catch(() => {})
    return () => { dead = true }
  }, [owner, relayUp, accts.length])

  useEffect(() => {
    if (!open) return
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [open])

  const connect = async () => {
    const k = input.trim()
    if (!k || busy) return
    setBusy(true); setError('')
    try {
      const d = await fetchAccts(k)
      localStorage.setItem(OWNER, k)
      setOwner(k); setAccts(Array.isArray(d) ? d : []); setInput(''); setOpen(false)
    } catch (s) {
      setError(s === 403 ? 'Invalid key' : s === 503 ? 'Schwab relay offline' : 'Connect failed')
    } finally { setBusy(false) }
  }
  const disconnect = () => {
    localStorage.removeItem(OWNER)
    setOwner(''); setAccts([]); setOpen(false)
  }

  if (owner && accts.length) {
    const i = idx % accts.length
    const val = fmtVal(accts[i].value)
    return (
      <div className="local-wallet-wrapper" ref={ref} style={{ display: 'flex', alignItems: 'center' }}>
        {val && (
          <button className="lw-lock-mini" title={hide ? 'Show value' : 'Hide value'}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px', color: 'inherit', opacity: 0.7 }}
            onClick={() => { const h = !hide; setHide(h); localStorage.setItem(HIDE, h ? '1' : '0') }}>
            {hide ? '\u{1F648}' : '\u{1F441}'}
          </button>
        )}
        {val && <span className="wallet-balance" style={{ color: '#46d41f', margin: 0 }}>{hide ? '######' : val}</span>}
        {children}
        <button className="local-wallet-btn connected" onClick={() => setOpen(o => !o)}
          title={`Schwab ${accts[i].masked}`}>
          {accts[i].masked}
        </button>
        {open && (
          <div className="local-wallet-dropdown">
            <div className="lw-dropdown-title">Schwab</div>
            {accts.map((a, n) => (
              <button key={n} className={`lw-dropdown-action${n === i ? ' primary' : ''}`}
                onClick={() => { setIdx(n); localStorage.setItem(KEY, String(n)); setOpen(false) }}>
                {a.masked}{fmtVal(a.value) ? `  ${hide ? '######' : fmtVal(a.value)}` : ''}
              </button>
            ))}
            <button className="lw-dropdown-action danger" onClick={disconnect}>Disconnect</button>
          </div>
        )}
      </div>
    )
  }
  if (owner) return children ?? null   // saved key, accounts loading or relay down

  return (
    <>{children}<div className="local-wallet-wrapper" ref={ref}>
      <button className="local-wallet-btn locked" onClick={() => setOpen(o => !o)}>
        <span className="lw-btn-label">Connect Schwab</span>
      </button>
      {open && (
        <div className="local-wallet-dropdown">
          <div className="lw-dropdown-title">Connect Schwab</div>
          <input className="lw-input" type="password" placeholder="Owner key" autoFocus
            value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') connect() }} />
          {error && <div className="lw-error">{error}</div>}
          <button className="lw-dropdown-action primary" onClick={connect} disabled={busy || !input.trim()}>
            {busy ? 'Connecting…' : 'Connect'}
          </button>
        </div>
      )}
    </div></>
  )
}
